import React, { useEffect, useState } from 'react';
import { MessageSquare, Plus, X, Menu } from 'lucide-react';
import { ConversationSummary } from '../../types/chat';
import { getConversations } from '../../services/api';
import {
  SidebarContainer,
  SidebarHeader,
  SidebarTitle,
  NewChatButton,
  ConversationList,
  ConversationItem,
  ConversationTitle,
  ConversationMeta,
  EmptyState,
  LoadingState,
  ToggleButton,
  CloseButton,
} from './styles';

interface ConversationSidebarProps {
  userId: string;
  currentSessionId: string;
  onSelectConversation: (sessionId: string) => void;
  onNewConversation: () => void;
  isOpen: boolean;
  onToggle: () => void;
}

const ConversationSidebar: React.FC<ConversationSidebarProps> = ({
  userId,
  currentSessionId,
  onSelectConversation,
  onNewConversation,
  isOpen,
  onToggle,
}) => {
  const [conversations, setConversations] = useState<ConversationSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadConversations = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await getConversations(userId);
      setConversations(response.conversations);
    } catch (err) {
      console.error('Failed to load conversations:', err);
      setError('Failed to load conversations');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      loadConversations();
    }
  }, [userId, isOpen]);

  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);

    if (diffInHours < 1) {
      return 'Just now';
    } else if (diffInHours < 24) {
      return `${Math.floor(diffInHours)}h ago`;
    } else if (diffInHours < 168) {
      return `${Math.floor(diffInHours / 24)}d ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  return (
    <>
      {!isOpen && (
        <ToggleButton onClick={onToggle}>
          <Menu size={20} />
        </ToggleButton>
      )}

      <SidebarContainer $isOpen={isOpen}>
        <SidebarHeader>
          <SidebarTitle>
            <MessageSquare size={20} />
            Conversations
          </SidebarTitle>
          <div style={{ display: 'flex', gap: '8px' }}>
            <NewChatButton onClick={onNewConversation} title="New conversation">
              <Plus size={18} />
            </NewChatButton>
            <CloseButton onClick={onToggle} title="Close sidebar">
              <X size={18} />
            </CloseButton>
          </div>
        </SidebarHeader>

        {isLoading ? (
          <LoadingState>
            <MessageSquare size={24} />
            Loading conversations...
          </LoadingState>
        ) : error ? (
          <EmptyState>
            <MessageSquare size={32} />
            {error}
          </EmptyState>
        ) : conversations.length === 0 ? (
          <EmptyState>
            <MessageSquare size={32} />
            No conversations yet
            <p>Start a new chat to begin</p>
          </EmptyState>
        ) : (
          <ConversationList>
            {conversations.map((conv) => (
              <ConversationItem
                key={conv.session_id}
                $isActive={conv.session_id === currentSessionId}
                onClick={() => onSelectConversation(conv.session_id)}
              >
                <ConversationTitle>{conv.title}</ConversationTitle>
                <ConversationMeta>
                  {conv.message_count} message{conv.message_count !== 1 ? 's' : ''} •{' '}
                  {formatTime(conv.last_message_time)}
                </ConversationMeta>
              </ConversationItem>
            ))}
          </ConversationList>
        )}
      </SidebarContainer>
    </>
  );
};

export default ConversationSidebar;
